import { useState } from "react";
import { useBoard } from "@/hooks/useBoard";
import { act as ccAct, dispatchFromCard } from "@/lib/ccApi";
import { AddWorkBar } from "@/screens/cc/AddWorkBar";
import { Card, Label, MonoMeta, StateWord } from "@/design/primitives";

// ── The idea dock (P3→P4): Jeff's ideas, wherever he typed them ──────────────
// One component + one store (work board first, goals intake next): capture
// plain words → Kit's proposal rides the row → convert: "Make it a project"
// (lands on Projects, the one home of project rows) or "One-off task" (back to
// the Desk as a plain question). Shared by Work and the Desk (P4 merge).
const IdeaDockRow = ({ item, board: ideaBoard, onFlash, desktop }) => {
  void desktop;
  const p = item.payload || {};
  const prop = p.kit_goal_proposal || null;
  const [text, setText] = useState(
    (p.title || "") + (p.why ? ` — ${p.why}` : ""),
  );
  const [busy, setBusy] = useState(false);
  const [converted, setConverted] = useState(null);
  // Phase 9: Run on Studio — the card's own words become real Studio work.
  // `routed` holds the calm confirmation (task id tail); a refusal shows the
  // backend's reason verbatim — the desk never improvises.
  const [routed, setRouted] = useState(null);
  const [runError, setRunError] = useState(null);
  // F5: drill rows (lane-typed test fixtures like "slot drill 1") render quiet —
  // grouped visually apart from real ideas, never competing for the eye.
  const isDrill = /\bdrill\b/i.test(item.payload?.title || item.payload?.why || "");
  const [moreOpen, setMoreOpen] = useState(false);

  const refine = async () => {
    setBusy(true);
    try {
      const ok = await ideaBoard.act("note", item.slug, text.trim());
      if (ok)
        onFlash("Saved — Kit reads it and brings the goal wording back here.");
    } finally {
      setBusy(false);
    }
  };

  const promote = async () => {
    setBusy(true);
    try {
      // Projects is the one home of project rows (P3): the acting board carries
      // the idea row; the project lands on Projects either way.
      const res = await ccAct({
        verb: "promoteIdea",
        board: ideaBoard.boardName || "goals",
        target: item.slug,
        value: (prop?.goal || text).trim(),
        craft: {
          goal: (prop?.goal || text).trim(),
          phases: prop?.phases_sketch || [],
        },
      });
      if (res) {
        setConverted("project");
        onFlash(
          "It's a project now — shaped with Kit's proposed wording. It lives on Projects.",
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const toTask = async () => {
    setBusy(true);
    try {
      // A one-off: back to the Desk as a plain question, wording kept.
      const res = await ccAct({
        verb: "promoteIdea",
        board: ideaBoard.boardName || "goals",
        target: item.slug,
        value: text.trim(),
        craft: {},
      });
      if (res) {
        setConverted("task");
        onFlash(
          "Handed to Kit as a one-off — it lands on the Desk, not as a full project.",
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const runOnStudio = async () => {
    setBusy(true);
    setRunError(null);
    try {
      const res = await dispatchFromCard(item.slug, ideaBoard.boardName || "goals");
      if (res?.ok) {
        setRouted(res);
        onFlash("Queued on the Studio — it lands in review when done.");
      }
    } catch (e) {
      setRunError(e.message || "Dispatch didn't go through.");
    } finally {
      setBusy(false);
    }
  };

  if (converted) {
    return (
      <Card
        quiet
        className="px-4 py-3"
        data-testid={`idea-dock-converted-${item.slug}`}
      >
        <p className="text-[0.8125rem] text-os-ink-3">
          {converted === "project"
            ? "Converted — it lives on Projects now."
            : "Handed off as a one-off task."}
        </p>
      </Card>
    );
  }

  return (
    <div
      className={
        isDrill
          ? "rounded-card border border-os-border bg-os-wash px-4 py-3 opacity-80"
          : "rounded-card border border-os-border p-4"
      }
      data-testid={`idea-dock-row-${item.slug}`}
      data-drill={isDrill ? "true" : undefined}
    >
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        className="w-full resize-none bg-transparent text-[0.875rem] leading-relaxed text-os-ink outline-none"
        data-testid={`idea-dock-text-${item.slug}`}
      />
      {/* F5 (desk review 2026-10-29): ONE primary verb where Jeff reads —
          "Save for Kit". The other three verbs collapse into a More menu so a
          card never offers four equal buttons (the what-do-I-press haze).
          Tests keep their ids: the buttons stay in the DOM, hidden until More. */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={refine}
          disabled={busy || !text.trim()}
          className="rounded-md border border-os-border-strong bg-os-ink px-3 py-1 font-mono text-[0.71875rem] text-white hover:opacity-90 disabled:opacity-50"
          data-testid={`idea-dock-refine-${item.slug}`}
        >
          {busy ? "Saving…" : "Save for Kit"}
        </button>
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          aria-expanded={moreOpen}
          className="rounded-md border border-os-border px-3 py-1 font-mono text-[0.71875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink"
          data-testid={`idea-dock-more-${item.slug}`}
        >
          {moreOpen ? "Less" : "More"}
        </button>
        <div
          className={
            moreOpen
              ? "flex flex-wrap items-center gap-2"
              : "hidden flex-wrap items-center gap-2"
          }
          data-testid={`idea-dock-more-menu-${item.slug}`}
        >
          <button
            type="button"
            onClick={runOnStudio}
            disabled={busy}
            className="rounded-md border border-os-border px-3 py-1 font-mono text-[0.71875rem] text-os-ink hover:border-os-border-strong disabled:opacity-50"
            data-testid={`idea-dock-run-${item.slug}`}
          >
            {busy ? "Queuing…" : "Run on Studio"}
          </button>
          <button
            type="button"
            onClick={promote}
            disabled={busy || !text.trim()}
            className="rounded-md border border-os-border-strong bg-os-ink px-3 py-1 font-mono text-[0.71875rem] text-white hover:opacity-90 disabled:opacity-50"
            data-testid={`idea-dock-project-${item.slug}`}
          >
            Make it a project
          </button>
          <button
            type="button"
            onClick={toTask}
            disabled={busy || !text.trim()}
            className="rounded-md border border-os-border px-3 py-1 font-mono text-[0.71875rem] text-os-ink hover:border-os-border-strong disabled:opacity-50"
            data-testid={`idea-dock-task-${item.slug}`}
          >
            One-off task
          </button>
        </div>
      </div>
      {prop && (
        <div
          className="mt-2 rounded-md border border-os-border bg-[var(--os-parchment,#faf7f2)] px-3 py-2.5"
          data-testid={`idea-dock-proposal-${item.slug}`}
        >
          <p className="text-[0.875rem] font-semibold leading-[1.5] text-os-ink">
            {prop.goal}
          </p>
          {prop.why_fit && (
            <p className="mt-1 text-[0.8125rem] leading-[1.5] text-os-ink-2">
              {prop.why_fit}
            </p>
          )}
          <p className="mt-1.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3">
            Kit's proposed wording — "Make it a project" uses it as-is
          </p>
        </div>
      )}
      {routed && (
        <p
          className="mt-2 border-t border-os-border pt-2 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-green"
          data-testid={`idea-dock-routed-${item.slug}`}
        >
          Routed to the Studio as {routed.task_id?.slice(0, 8)} · {routed.model}
        </p>
      )}
      {runError && (
        <p
          className="mt-2 border-t border-os-border pt-2 text-[0.8125rem] text-os-amber"
          data-testid={`idea-dock-run-error-${item.slug}`}
        >
          {runError}
        </p>
      )}
      {(p.jeff?.notes?.length || 0) > 0 && (
        <p className="mt-2 border-t border-os-border pt-2 text-[0.8125rem] text-os-ink-3">
          {p.jeff.notes.length} note{p.jeff.notes.length === 1 ? "" : "s"} saved
          — Kit reads them on the next pass.
        </p>
      )}
    </div>
  );
};

// ── Extra dock row: work that has no goal yet (4→2 council, 2026-10-22) ────
// These are not typed ideas — they are real pieces of work whose goal was
// never written down. The one honest action is to set them aside; the goal
// itself is a thinking job, not a button.
const ExtraDockRow = ({ item, board: extraBoard, onFlash }) => {
  const p = item.payload || {};
  const [busy, setBusy] = useState(false);
  const title = p.title || item.slug;
  return (
    <div
      className="rounded-card border border-os-border p-4"
      data-testid={`idea-dock-extra-${item.slug}`}
      data-state={item.state}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="min-w-0 flex-1 truncate text-[0.9375rem] font-medium text-os-ink">
          {title}
        </p>
        <StateWord tone="amber">Needs a goal</StateWord>
      </div>
      {p.why && (
        <p className="mt-1 text-[0.8125rem] leading-[1.4] text-os-ink-2">{p.why}</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const ok = await extraBoard.act("archiveAny", item.slug);
              if (ok) onFlash("Set aside — it stops asking for a goal.");
            } finally {
              setBusy(false);
            }
          }}
          className="rounded-md border border-os-border px-3 py-1 font-mono text-[0.71875rem] text-os-ink hover:border-os-border-strong disabled:opacity-50"
          data-testid={`idea-dock-extra-set-aside-${item.slug}`}
        >
          {busy ? "Setting it aside…" : "Set aside"}
        </button>
      </div>
    </div>
  );
};

export const IdeaDock = ({ onFlash, desktop, extraItems = null, extraLabel = "" }) => {
  // The dock is Jeff's ideas, wherever he typed them (Work first, goals intake
  // next) — one loop, one list.
  //
  // extraItems (4→2 council, 2026-10-22): the Needs Me page folds the
  // needs-a-goal dock into the same capture dock — the work that has no goal
  // yet sits with the ideas, one list, deduped. The Desk and Work pages pass
  // nothing and render exactly as before.
  const workBoard = useBoard("work");
  const goalsBoard = useBoard("goals");
  const live = workBoard.live || goalsBoard.live;
  const ideas = workBoard.live
    ? (workBoard.items || []).filter(
        (r) => r.kind === "idea" && r.state !== "ARCHIVED",
      )
    : goalsBoard.live
      ? (goalsBoard.items || []).filter(
          (r) => r.kind === "idea" && r.state !== "ARCHIVED",
        )
      : [];
  const boardObj = workBoard.live ? workBoard : goalsBoard;
  const boardName = workBoard.live ? "work" : "goals";

  // One list: the typed ideas, then the work with no goal yet — never the
  // same piece of work twice.
  const extras = (extraItems || []).filter(
    (x) =>
      x &&
      x.slug &&
      !ideas.some((r) => r.slug === x.slug) &&
      (x.payload?.title || x.title || "") !== "",
  );
  const dockCount = ideas.length + extras.length;

  if (!live) {
    return (
      <Card quiet className="px-5 py-4" data-testid="idea-dock-offline">
        <p className="text-[0.8125rem] text-os-ink-3">
          Actions are offline this visit — the dock comes back the moment the
          desk is reachable.
        </p>
      </Card>
    );
  }

  return (
    <section data-testid="idea-dock" className="flex flex-col gap-3">
      <Label as="h2">The idea dock — get it out of your head</Label>
      <p className="text-[0.8125rem] text-os-ink-3">
        Type it plain. Kit works the wording with you here; when it's ready it
        becomes a project (on Projects) or a one-off task (on the Desk).
      </p>
      <AddWorkBar
        onAdded={() =>
          onFlash("Idea captured — Kit reads it on the next pass.")
        }
      />
      {dockCount === 0 ? (
        <Card quiet className="px-5 py-4">
          <p className="text-[0.875rem] text-os-ink-2">
            Nothing waiting — say the words above and they land here.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {ideas.map((r) => (
            <IdeaDockRow
              key={r.slug}
              item={r}
              board={{ ...boardObj, boardName }}
              onFlash={onFlash}
              desktop={desktop}
            />
          ))}
          {extras.map((x) => (
            <ExtraDockRow
              key={x.slug}
              item={x}
              // The needs-a-goal work lives on the work board — it must be
              // set aside there, never on the fallback board.
              board={workBoard}
              onFlash={onFlash}
            />
          ))}
          {extraLabel && (
            <MonoMeta as="p" data-testid="idea-dock-extra-label">
              {extraLabel}
            </MonoMeta>
          )}
        </div>
      )}
    </section>
  );
};

export default IdeaDock;
